import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

import { ScreenMessage } from '../components/ScreenMessage';
import { Loader } from '../components/Loader';
import { enableScroll, hideTermsPrompt } from '../domains/UI/store/slice';
import Footer from '../Footer';
import { PromptModal } from '../GlobalComponents/PromptModal';
import { PromptBanner } from '../GlobalComponents/PromptBanner';
import { Header } from '../Header/Header';
import { MobileHeader } from '../Header/components/MobileHeader';
import { useAppDispatch, useAppSelector } from '../reduxstore/hooks';
import { web } from '../themes';
import { AlertsContainer } from './components/AlertsContainer';
import { BottomNavBar } from './components/BottomNavBar';
import { IsolatePageLoad } from './components/IsolatePageLoad';
import { Router } from './Router';
import { StoreSelector } from '../GlobalComponents/StoreSelector';
import { ProductFooter } from '../Product/components/ProductFooter';
import { useIntersection } from 'react-use';
import { useNavigate } from 'react-router-dom';
export const Layout: React.FC = () => {
	const disableScroll = useAppSelector(
		state => state.uiReducer.disableScroll
	);
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { showTermsPrompt } = useAppSelector(state => state.uiReducer);
	const showPrompt = useAppSelector(
		state => state.uiReducer.showBannerPrompt
	);
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const showProductFooter = pathname.includes('product');
	const isEditView = pathname.includes('edit');

	const fixFooterThresholdRef = useRef<HTMLDivElement>(null);

	const bodyRef = useRef<HTMLDivElement>(null);

	const desktopFooterRef = useRef<HTMLDivElement>(null);

	const toggleTermsPrompt = () => {
		return dispatch(hideTermsPrompt());
	};

	const moveTo = (url: string) => {
		navigate(url);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	// for MobileNav scroll lock
	useEffect(() => {
		dispatch(enableScroll());
	}, [location, dispatch]);

	useEffect(() => {
		if (disableScroll) {
			document.body.style.overflowY = 'hidden';
		} else {
			document.body.style.overflowY = 'scroll';
		}

		return () => {
			document.body.style.overflowY = 'scroll';
		};
	}, [disableScroll]);

	// for desktop sticky footer
	const intersection = useIntersection(desktopFooterRef, {
		root: null,
		rootMargin: '160px',
		threshold: 1
	});

	return (
		<Loader title="Layout">
			<LayoutWrapper id="layout">
				<ThemeProvider theme={web}>
					<div className="app">
						<IsolatePageLoad />
						<ScreenMessage />
						<StoreSelector />
						<LayoutContainer id="layout-container">
							<div className="d-lg-none d-block">
								<MobileHeader />
							</div>
							{showPrompt && (
								<PromptBanner
									$background="#ffb4aa"
									text="We noticed an issue with the email address associated with your account. Please update your profile to continue receiving coupons and order status updates."
									to="/account/info"
								/>
							)}
							{showTermsPrompt && (
								<PromptModal
									isOpen={showTermsPrompt}
									toggleHandler={toggleTermsPrompt}
								/>
							)}

							<div className="d-none d-lg-block">
								<Header />
							</div>
							<div>
								<Body id="body" ref={bodyRef}>
									<Router />
								</Body>
							</div>
						</LayoutContainer>
						<AlertsContainer />
						{showProductFooter && (
							<div
								style={{
									height: '80px'
								}}
							>
								<ProductFooter
									isEdit={isEditView}
									fixPosition={
										intersection &&
										intersection.intersectionRatio < 1
											? true
											: false
									}
								/>
							</div>
						)}
						<div
							className="d-lg-none d-flex flex-column"
							ref={fixFooterThresholdRef}
						>
							<BottomNavBar />
						</div>

						<div
							className="d-none d-lg-flex flex-column"
							ref={desktopFooterRef}
						>
							<Footer moveTo={moveTo} />
						</div>
					</div>
				</ThemeProvider>
			</LayoutWrapper>
		</Loader>
	);
};

const LayoutContainer = styled.div`
	min-height: 100vh;
	@media (max-width: 992px) {
		min-height: calc(100vh - 90px - 82px);
		padding-bottom: 82px;
	}
`;

const Body = styled.div`
	height: 100%;
	width: 100%;
	margin: 36px 0 60px 0;
	display: flex;
	flex-direction: column;
	justify-content: center;

	@media (max-width: 1200px) {
		padding: 24px;
	}

	@media (max-width: 768px) {
		margin: 0;
		padding: 16px;
		padding-top: 0;
		flex-direction: column;
	}
`;

const LayoutWrapper = styled.div`
	@media (max-width: 992px) {
		padding-bottom: 3rem;
	}
`;
